import React, {
  useContext,
  useState,
  useMemo,
  useEffect,
  useCallback,
} from 'react'
import { BackgroundActivityNotification } from '../types/BackgroundActivityTypes'
import { UserContext } from '../../../App/UserContext'
import { useQuery } from '@apollo/client/react'
import { GET_BACKGROUND_ACTIVITY_NOTIFICATIONS } from 'graphql/queries/backgroundActivity/backgroundActivityQueries'

const POLL_INTERVAL_MS = 10000

type SparkUserLike = { userProfileToken?: string } | null

// Overlay value semantics:
//   - BackgroundActivityNotification  -> locally added/updated entry
//   - null                            -> tombstone (locally removed)
type Overlay = Record<string, BackgroundActivityNotification | null>

export const BackgroundActivityContext = React.createContext({
  allActivities: [] as BackgroundActivityNotification[],
  addActivity: (activity: BackgroundActivityNotification) => {},
  removeActivity: (activityId: string) => {},
  generatedTime: '',
  callSetGenerateTime: () => {},
})

export const BackgroundActivityProvider: React.FC<{
  children: React.ReactNode
}> = ({ children }: { children: React.ReactNode }) => {
  const { sparkUser } = useContext(UserContext)
  const userProfileToken = (sparkUser as SparkUserLike)?.userProfileToken
  const [overlay, setOverlay] = useState<Overlay>({})
  const [generatedTime, setGeneratedTime] = useState('')

  const addActivity = useCallback(
    (activity: BackgroundActivityNotification) => {
      setOverlay((prev) => ({ ...prev, [activity.id]: activity }))
    },
    [],
  )

  const removeActivity = useCallback((activityId: string) => {
    setOverlay((prev) => ({ ...prev, [activityId]: null }))
  }, [])

  const callSetGenerateTime = useCallback(() => {
    setGeneratedTime(new Date().toISOString())
  }, [])

  const { data } = useQuery(GET_BACKGROUND_ACTIVITY_NOTIFICATIONS, {
    fetchPolicy: 'cache-and-network',
    skip: !userProfileToken,
    pollInterval: POLL_INTERVAL_MS,
    notifyOnNetworkStatusChange: false,
  })

  const currentNotificationsFromGQL = useMemo(() => {
    return (data?.getBackgroundActivityNotifications?.content ||
      []) as BackgroundActivityNotification[]
  }, [data])

  // Refresh the "last updated" stamp whenever a poll response arrives.
  useEffect(() => {
    setGeneratedTime(new Date().toISOString())
  }, [currentNotificationsFromGQL])

  // Garbage-collect overlay entries the server has caught up on so the
  // overlay does not grow unbounded across polls.
  useEffect(() => {
    setOverlay((prev) => {
      const serverById = new Map(
        currentNotificationsFromGQL.map((n) => [n.id, n]),
      )
      let changed = false
      const next: Overlay = {}
      for (const [id, entry] of Object.entries(prev)) {
        const serverHit = serverById.get(id)
        const stillNeeded =
          entry === null
            ? Boolean(serverHit) // tombstone only matters while server still returns it
            : !serverHit // optimistic add only matters until server returns it
        if (stillNeeded) {
          next[id] = entry
        } else {
          changed = true
        }
      }
      return changed ? next : prev
    })
  }, [currentNotificationsFromGQL])

  // Derived list: server data with local overlay applied on top.
  const allActivities = useMemo<BackgroundActivityNotification[]>(() => {
    const server = currentNotificationsFromGQL
    const serverIds = new Set(server.map((s) => s.id))
    const merged = server
      .map((s) => (s.id in overlay ? overlay[s.id] : s))
      .filter((a): a is BackgroundActivityNotification => a !== null)
    const extras = Object.entries(overlay)
      .filter(
        (entry): entry is [string, BackgroundActivityNotification] =>
          entry[1] !== null && !serverIds.has(entry[0]),
      )
      .map(([, v]) => v)
    return [...merged, ...extras]
  }, [currentNotificationsFromGQL, overlay])

  const value = useMemo(
    () => ({
      allActivities,
      addActivity,
      removeActivity,
      generatedTime,
      callSetGenerateTime,
    }),
    [
      allActivities,
      addActivity,
      removeActivity,
      generatedTime,
      callSetGenerateTime,
    ],
  )

  return (
    <BackgroundActivityContext.Provider value={value}>
      {children}
    </BackgroundActivityContext.Provider>
  )
}
